import { connect, Mapping } from 'react-refetch';
import interceptorRegistry from './interceptors/interceptorRegistry';

/**
 * This function is an alternative fetch mechanism for react-fetch that can handle
 * requests whose responses require elaborate data manipulation. Using this pattern
 * heavy business logic related processing can be decoupled from the components.
 *
 * An interceptor can be registered for a specific path element using the src/helpers/interceptors/interceptorRegistry
 *
 * For example, if a component would use a mixture of normal API calls and calls whose response
 * needs further processing a react-refetch configuration such as this can be used:
 * {
 *   customList: {
 *     url: `/api/customEntityLists/31745971-84dc-4279-b707-c60ab5e64a03`,
 *     force: true,
 *     refreshing: true,
 *   },
 *   interceptedCustomList: {
 *     url: `/test/31745971-84dc-4279-b707-c60ab5e64a03`,
 *     force: true,
 *     refreshing: true,
 *     interceptionData: {
 *       additionalData: 'data'
 *     }
 *   },
 * };
 *
 * The interceptionData property is a way to pass on additional to the interceptor that is needed
 * to execute the API call.
 *
 * The interceptorRegistry would be added to like this to handle the /test/ path:
 *
 * const interceptorRegistry: {
 * [key: string]: (input: RequestInfo, init: RequestInit, interceptionData: any) => Promise<Response>;
 * } = {
 *   // this is the new interceptor ===>>>
 *   test: testInterceptor,
 *
 *   api: sentryInterceptor,
 *   notifications: sentryInterceptor,
 *   kv: sentryInterceptor,
 *   auth: sentryInterceptor,
 * };
 *
 */
export async function interceptFetch(
  path: string,
  input: RequestInfo,
  init: RequestInit | undefined,
  interceptionData: any
): Promise<Response> {
  const idx = path.indexOf('?');
  let pathInterceptor = idx && idx < 0 ? path : path.substr(0, idx);
  if (pathInterceptor.startsWith('/')) {
    pathInterceptor = pathInterceptor?.substr(1);
  }
  const interceptor = interceptorRegistry[pathInterceptor || ''];
  if (!interceptor) {
    const errorMessage = `No interceptor was registered for the name '${pathInterceptor}'`;
    console.error(errorMessage);
    throw new Error(errorMessage);
  }
  return interceptor(input, init, interceptionData);
}

function buildRequest(mapping: Mapping<any, any>): any {
  const { interceptionData } = mapping;

  // Here is the trick where we can pass on the interception data
  // to our interceptors. We simply create the fetch method with our
  // interceptFetch wrapped in a closure that contains the interceptor data.
  // This call is done for each request that is executed so we always pass
  // on the correct data to the interceptor
  function customFetch(
    request: RequestInfo,
    init: RequestInit | undefined
  ): any {
    return interceptFetch(mapping.url ?? '', request, init, interceptionData);
  }
  (mapping as any).fetch = customFetch;

  const request = new (mapping as any).Request(mapping.url, {
    method: mapping.method,
    headers: mapping.headers,
    credentials: mapping.credentials,
    redirect: mapping.redirect,
    mode: mapping.mode,
    body: mapping.body,
  });
  return request;
}

export default connect.defaults({
  buildRequest: buildRequest,
});
